package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import co.maytes.standupbot.dashboard.components.motion.Presence
import co.maytes.standupbot.dashboard.components.motion.presenceClasses
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.ZIndex
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.css.CSSNumeric
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.asList
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

enum class PopoverPlacement(val transformOrigin: String) {
    BottomStart("top left"),
    BottomEnd("top right"),
    BottomFill("top center"),
    TopStart("bottom left"),
    TopEnd("bottom right"),
}

private const val POPOVER_CLASS = "sb-pop"
private const val TRIGGER_FALLBACK_SELECTOR = "[aria-haspopup], [aria-expanded]"

private class SurfaceRef(var element: HTMLElement? = null, var composedOnce: Boolean = false)

private const val FOCUSABLE_SELECTOR =
    "input:not([disabled]),button:not([disabled]),select:not([disabled]),textarea:not([disabled])," +
        "a[href],[tabindex]:not([tabindex='-1'])"

@Composable
fun Popover(
    open: Boolean,
    placement: PopoverPlacement,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    offset: CSSNumeric = Spacing.xs,
    role: String? = null,
    ariaLabel: String? = null,
    content: @Composable () -> Unit,
) {
    val surface = remember { SurfaceRef() }
    val latestOnDismiss by rememberUpdatedState(onDismiss)

    DisposableEffect(open) {
        val openedAfterFirstComposition = surface.composedOnce
        surface.composedOnce = true
        if (!open) return@DisposableEffect onDispose { }

        val anchor = { surface.element?.parentElement as? HTMLElement }
        val trigger = { resolveTrigger(anchor(), surface.element) }
        var dismissedByPointer = false

        val onKeyDown: (Event) -> Unit = { event ->
            if (event is KeyboardEvent && event.key == "Escape") {
                event.preventDefault()
                event.stopPropagation()
                latestOnDismiss()
            }
        }
        val onPointerDown: (Event) -> Unit = { event ->
            val target = event.target as? Node
            val inside = target != null && anchor()?.contains(target) == true
            if (!inside) {
                dismissedByPointer = true
                latestOnDismiss()
            }
        }
        document.addEventListener("keydown", onKeyDown, true)
        document.addEventListener("pointerdown", onPointerDown, true)
        if (openedAfterFirstComposition) focusAfterRender { firstFocusable(surface.element) }

        onDispose {
            document.removeEventListener("keydown", onKeyDown, true)
            document.removeEventListener("pointerdown", onPointerDown, true)
            if (shouldReturnFocus(surface.element, dismissedByPointer)) trigger()?.focus()
        }
    }

    Presence(visible = open, enterMs = Motion.POPOVER_IN_MS, exitMs = Motion.POPOVER_OUT_MS) { presence ->
        Div(
            attrs = Modifier
                .presenceClasses(POPOVER_CLASS, presence)
                .styleModifier { anchorTo(placement, offset) }
                .then(modifier)
                .toAttrs {
                    if (role != null) attr("role", role)
                    if (ariaLabel != null) attr("aria-label", ariaLabel)
                    ref { element ->
                        surface.element = element
                        onDispose { if (surface.element === element) surface.element = null }
                    }
                },
        ) {
            content()
        }
    }
}

private fun StyleScope.anchorTo(placement: PopoverPlacement, offset: CSSNumeric) {
    property("position", "absolute")
    property("z-index", "${ZIndex.dropdown}")
    property("transform-origin", placement.transformOrigin)
    val clearance = "calc(100% + $offset)"
    when (placement) {
        PopoverPlacement.BottomStart -> {
            property("top", clearance)
            property("left", "0")
        }
        PopoverPlacement.BottomEnd -> {
            property("top", clearance)
            property("right", "0")
        }
        PopoverPlacement.BottomFill -> {
            property("top", clearance)
            property("left", "0")
            property("right", "0")
        }
        PopoverPlacement.TopStart -> {
            property("bottom", clearance)
            property("left", "0")
        }
        PopoverPlacement.TopEnd -> {
            property("bottom", clearance)
            property("right", "0")
        }
    }
}

private fun shouldReturnFocus(surface: HTMLElement?, dismissedByPointer: Boolean): Boolean {
    val active = document.activeElement
    val focusInsideSurface = surface != null && active != null && surface.contains(active)
    val focusLost = active == null || active === document.body
    return focusInsideSurface || (focusLost && !dismissedByPointer)
}

private fun resolveTrigger(anchor: HTMLElement?, surface: HTMLElement?): HTMLElement? =
    anchor?.querySelectorAll(TRIGGER_FALLBACK_SELECTOR)?.asList()
        ?.filterIsInstance<HTMLElement>()
        ?.firstOrNull { surface == null || !surface.contains(it) }

private fun firstFocusable(surface: HTMLElement?): HTMLElement? =
    surface?.querySelector(FOCUSABLE_SELECTOR) as? HTMLElement
